// AdminStatsPanel.jsx
import React, { useMemo } from "react";


const fmtCurrency = (n, currency = "EUR") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const AdminStatsPanel = ({ stats, doctorsById }) => {
  const {
    patientsCount = 0,
    totalAppointments = 0,
    apptStatus = {},
    revenue = {},
    services = {},
    today = {},
    logins = {},
  } = stats || {};

  const totalDoctors = Object.keys(doctorsById || {}).length;

  const {
    pending = 0,
    confirmed = 0,
    completed = 0,
    cancelled = 0,
  } = apptStatus || {};

  const {
    total: totalRevenue = 0,
    total30d = 0,
    prev30d = 0,
  } = revenue || {};

  const { catalog = [] } = services || {};

  // Today activity (optional, safe defaults)
  const {
    appointments: appointmentsToday = 0,
    pending: pendingToday = 0,
    confirmed: confirmedToday = 0,
    completed: completedToday = 0,
    cancelled: cancelledToday = 0,
    newPatients: newPatientsToday = 0,
  } = today || {};

  const { patientsToday = 0, doctorsToday = 0 } = logins || {};

  const todayLabel = new Date().toLocaleDateString("sq-AL", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });

  // Derived metrics (memoized)
  const derived = useMemo(() => {
    const conversion =
      totalAppointments > 0 ? (completed / totalAppointments) * 100 : 0;

    const cancelRate =
      totalAppointments > 0 ? (cancelled / totalAppointments) * 100 : 0;

    const pendingRate =
      totalAppointments > 0 ? (pending / totalAppointments) * 100 : 0;

    const confirmRate =
      totalAppointments > 0 ? (confirmed / totalAppointments) * 100 : 0;

    const avgRevenuePerPatient =
      patientsCount > 0 ? totalRevenue / patientsCount : 0;

    const avgRevenuePerDoctor =
      totalDoctors > 0 ? totalRevenue / totalDoctors : 0;

    const revDeltaPct =
      prev30d > 0 ? ((total30d - prev30d) / prev30d) * 100 : 0;

    const apptsPerPatient =
      patientsCount > 0 ? totalAppointments / patientsCount : 0;

    const apptsPerDoctor =
      totalDoctors > 0 ? totalAppointments / totalDoctors : 0;

    const revenuePerAppointment =
      totalAppointments > 0 ? totalRevenue / totalAppointments : 0;

    // specialties from doctor docs
    const specialtyMap = new Map();
    Object.values(doctorsById || {}).forEach((d) => {
      const spec = d.specialty || d.profile?.specialty || "Tjetër";
      const cur = specialtyMap.get(spec) || { count: 0, revenue: 0 };
      cur.count += 1;
      specialtyMap.set(spec, cur);
    });

    const specialtyRows = Array.from(specialtyMap.entries())
      .map(([name, meta]) => ({ name, ...meta }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Service catalogue stats
    const avgServicePrice =
      catalog.length > 0
        ? catalog.reduce((s, x) => s + Number(x.price || 0), 0) /
          catalog.length
        : 0;

    return {
      conversion,
      cancelRate,
      pendingRate,
      confirmRate,
      avgRevenuePerPatient,
      avgRevenuePerDoctor,
      revDeltaPct,
      specialtyRows,
      avgServicePrice,
      apptsPerPatient,
      apptsPerDoctor,
      revenuePerAppointment,
    };
  }, [
    totalAppointments,
    completed,
    cancelled,
    pending,
    confirmed,
    patientsCount,
    totalRevenue,
    totalDoctors,
    total30d,
    prev30d,
    catalog,
    doctorsById,
  ]);

  return (
    <div
      style={{
        marginBottom: 20,
        marginTop: 8,
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #e5e7eb",
        padding: 16,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontWeight: 800,
            fontSize: 15,
            color: "#111827",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "999px",
              background: derived.revDeltaPct >= 0 ? "#22c55e" : "#ef4444",
            }}
          />
          Admin Stats (Deep Dive)
        </div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          Pasqyrë agregate e performancës së platformës
        </div>
      </div>

      {/* Main grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 12,
        }}
      >
        {/* Block 1 – Conversion & cancellation */}
        <StatsCard title="Conversion (Booked → Completed)">
          <BigMetric value={`${derived.conversion.toFixed(1)}%`} />
          <SubLabel>
            {completed} completed nga {totalAppointments} takime
          </SubLabel>
          <SmallRow
            label="Cancellation rate"
            value={`${derived.cancelRate.toFixed(1)}%`}
          />
          <SmallRow
            label="Pending rate"
            value={`${derived.pendingRate.toFixed(1)}%`}
          />
          <SmallRow
            label="Confirmation rate"
            value={`${derived.confirmRate.toFixed(1)}%`}
          />
        </StatsCard>

        {/* Block 2 – Revenue efficiency */}
        <StatsCard title="Revenue Efficiency">
          <BigMetric value={fmtCurrency(derived.avgRevenuePerPatient)} />
          <SubLabel>Mesatarisht për pacient</SubLabel>
          <SmallRow
            label="Mesatarisht për doktor"
            value={fmtCurrency(derived.avgRevenuePerDoctor)}
          />
        </StatsCard>

        {/* Block 3 – Revenue 30d vs prev */}
        <StatsCard title="Revenue 30 ditët e fundit">
          <BigMetric value={fmtCurrency(total30d)} />
          <SubLabel>Periudha e fundit 30 ditore</SubLabel>
          <SmallRow
            label="Periudha e mëparshme"
            value={fmtCurrency(prev30d)}
          />
          <SmallRow
            label="Δ vs periudha e mëparshme"
            value={`${
              derived.revDeltaPct >= 0 ? "+" : ""
            }${derived.revDeltaPct.toFixed(1)}%`}
            highlight={true}
          />
        </StatsCard>

        {/* Block 4 – Services */}
        <StatsCard title="Shërbimet">
          <BigMetric value={services.total || 0} />
          <SubLabel>Totali i shërbimeve në katalog</SubLabel>
          <SmallRow
            label="Çmimi mesatar i shërbimit"
            value={fmtCurrency(derived.avgServicePrice)}
          />
        </StatsCard>

        {/* Block 5 – Appointment volume */}
        <StatsCard title="Volumi i Takimeve">
          <BigMetric value={totalAppointments} />
          <SubLabel>Të gjitha takimet (fetched)</SubLabel>
          <SmallRow
            label="Takime / pacient"
            value={derived.apptsPerPatient.toFixed(2)}
          />
          <SmallRow
            label="Takime / doktor"
            value={derived.apptsPerDoctor.toFixed(2)}
          />
          <SmallRow
            label="Revenue / takim"
            value={fmtCurrency(derived.revenuePerAppointment)}
          />
        </StatsCard>
      </div>

      {/* Today activity */}
      <div style={{ marginTop: 18 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 6,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>Aktiviteti i sotëm</span>
          <span style={{ fontSize: 11, color: "#6b7280" }}>{todayLabel}</span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 10,
          }}
        >
          {/* Today's appointments */}
          <StatsCard title="Takimet e sotme">
            <BigMetric value={appointmentsToday} />
            <SubLabel>Takime të planifikuara / kryera sot</SubLabel>
            <SmallRow
              label="Pending sot"
              value={pendingToday.toString()}
            />
            <SmallRow
              label="Confirmed sot"
              value={confirmedToday.toString()}
            />
            <SmallRow
              label="Completed sot"
              value={completedToday.toString()}
            />
            <SmallRow
              label="Cancelled sot"
              value={cancelledToday.toString()}
            />
            {newPatientsToday > 0 && (
              <SmallRow
                label="Pacientë të rinj sot"
                value={newPatientsToday.toString()}
              />
            )}
          </StatsCard>

          {/* Today's logged-in users */}
          <StatsCard title="Sot në platformë">
            <BigMetric value={patientsToday + doctorsToday} />
            <SubLabel>Totali i përdoruesve që janë futur sot</SubLabel>
            <SmallRow
              label="Pacientë të loguar sot"
              value={patientsToday.toString()}
            />
            <SmallRow
              label="Doktorë të loguar sot"
              value={doctorsToday.toString()}
            />
          </StatsCard>
        </div>
      </div>

      {/* Specialties section */}
      <div style={{ marginTop: 18 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 6,
          }}
        >
          Specialitetet kryesore (sipas numrit të doktorëve)
        </div>
        {!derived.specialtyRows.length ? (
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Nuk ka ende të dhëna për specialitete.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 8,
            }}
          >
            {derived.specialtyRows.map((s) => (
              <div
                key={s.name}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 10,
                  background: "#f9fafb",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#111827",
                    marginBottom: 4,
                  }}
                >
                  {s.name}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>
                  {s.count} doktorë
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ====== Small presentational pieces ====== */

const StatsCard = ({ title, children }) => (
  <div
    style={{
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: 12,
      background: "#fefefe",
      display: "grid",
      gap: 4,
    }}
  >
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: "#6b7280",
        marginBottom: 2,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const BigMetric = ({ value }) => (
  <div style={{ fontSize: 22, fontWeight: 800, color: "#111827" }}>
    {value}
  </div>
);

const SubLabel = ({ children }) => (
  <div style={{ fontSize: 11, color: "#9ca3af" }}>{children}</div>
);

const SmallRow = ({ label, value, highlight }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: 11,
      marginTop: 4,
    }}
  >
    <span style={{ color: "#6b7280" }}>{label}</span>
    <span
      style={{
        fontWeight: 700,
        color: highlight
          ? value.toString().startsWith("+")
            ? "#16a34a"
            : "#dc2626"
          : "#111827",
      }}
    >
      {value}
    </span>
  </div>
);

export default AdminStatsPanel;
